// OfferDetailDrawer — edit an offer/bid from the case-centric Offers
// module. Mirrors ViewingDetailDrawer: opened from an OfferBoard row,
// persists via PATCH /agent/bids/{id}, fires onSaved so the table
// re-hydrates. Covers the offer status plus the negotiation fields
// (seller counter + deadline + next step).

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateBid, type Bid, type BidStatus, type BidUpdate } from "@/lib/proagentApi";
import {
  Btn,
  Field,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalShell,
  Select,
  TextArea,
  TextInput,
} from "./ModalShell";

const STATUS_OPTIONS: { value: BidStatus; label: string }[] = [
  { value: "received", label: "Received" },
  { value: "countered", label: "Countered" },
  { value: "accepted", label: "Accepted" },
  { value: "rejected", label: "Rejected" },
  { value: "withdrawn", label: "Withdrawn" },
];

// datetime-local <input> wants YYYY-MM-DDTHH:MM in local tz; the wire
// is UTC ISO. Round-trip through Date both ways.
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

type Form = {
  bidder: string;
  amount: string;
  status: BidStatus;
  notes: string;
  sellerCounter: string;
  sellerCounterAt: string;
  counterDue: string;
  nextStep: string;
};

function formFromBid(b: Bid): Form {
  return {
    bidder: b.bidder_name ?? "",
    amount: b.amount != null ? String(b.amount) : "",
    status: b.status,
    notes: b.notes ?? "",
    sellerCounter: b.seller_counter != null ? String(b.seller_counter) : "",
    sellerCounterAt: toLocalInput(b.seller_counter_at),
    counterDue: toLocalInput(b.counter_due),
    nextStep: b.next_step ?? "",
  };
}

export function OfferDetailDrawer({
  open,
  bid,
  onClose,
  onSaved,
}: {
  open: boolean;
  bid: Bid | null;
  onClose: () => void;
  // Fired after a successful PATCH so the opener (the Offers table)
  // can re-fetch and show the persisted values without a reload.
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<Form | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !bid) return;
    setForm(formFromBid(bid));
    setSaving(false);
    setSaveError(null);
  }, [open, bid]);

  if (!bid || !form) return null;

  const set = <K extends keyof Form>(k: K, v: Form[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  // Persist the edited offer. seller_counter / *_at / counter_due are
  // only sent when filled (the pointer-patch backend treats omitted
  // fields as "leave unchanged" — clearing a set value isn't supported
  // yet, see SCRUM-7). status / notes / next_step always round-trip.
  async function handleSave() {
    if (!bid || !form) return;
    const amt = Number(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setSaveError(
        t("agent.flows.offer.needAmount", {
          defaultValue: "Offer amount must be a positive number.",
        }) as string,
      );
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const counter = Number(form.sellerCounter);
      const patch: BidUpdate = {
        bidder_name: form.bidder.trim(),
        amount: amt,
        status: form.status,
        notes: form.notes.trim(),
        next_step: form.nextStep.trim(),
      };
      if (form.sellerCounter.trim() && Number.isFinite(counter) && counter > 0) {
        patch.seller_counter = counter;
      }
      if (form.sellerCounterAt) {
        patch.seller_counter_at = new Date(form.sellerCounterAt).toISOString();
      }
      if (form.counterDue) {
        patch.counter_due = new Date(form.counterDue).toISOString();
      }
      await updateBid(bid.id, patch);
      onSaved?.();
      onClose();
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <ModalShell open={open} onClose={onClose} width={640} label="Offer detail">
      <ModalHeader
        eyebrow={t("agent.flows.offer.eyebrow", { defaultValue: "Offer" })}
        title={
          bid.bidder_name ||
          t("agent.flows.offer.fallback", { defaultValue: "Offer" })
        }
        hint={t("agent.flows.offer.hint", {
          defaultValue: "Update the offer status and negotiation details.",
        })}
        onClose={onClose}
      />

      <ModalBody>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t("agent.flows.offer.buyer", { defaultValue: "Buyer" })}>
            <TextInput
              value={form.bidder}
              onChange={(v) => set("bidder", v)}
              placeholder="Buyer name"
            />
          </Field>
          <Field label={t("agent.flows.offer.amount", { defaultValue: "Offer amount (S$)" })}>
            <TextInput
              mono
              type="number"
              step="1000"
              value={form.amount}
              onChange={(v) => set("amount", v)}
              placeholder="1420000"
            />
          </Field>
        </div>

        <Field label={t("agent.flows.offer.status", { defaultValue: "Status" })}>
          <Select
            value={form.status}
            onChange={(v) => set("status", (v || "received") as BidStatus)}
            options={STATUS_OPTIONS}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field
            label={t("agent.flows.offer.sellerCounter", {
              defaultValue: "Seller counter (S$)",
            })}
          >
            <TextInput
              mono
              type="number"
              step="1000"
              value={form.sellerCounter}
              onChange={(v) => set("sellerCounter", v)}
              placeholder="1465000"
            />
          </Field>
          <Field
            label={t("agent.flows.offer.sellerCounterAt", { defaultValue: "Countered on" })}
          >
            <TextInput
              type="datetime-local"
              value={form.sellerCounterAt}
              onChange={(v) => set("sellerCounterAt", v)}
            />
          </Field>
        </div>

        <Field label={t("agent.flows.offer.counterDue", { defaultValue: "Response due" })}>
          <TextInput
            type="datetime-local"
            value={form.counterDue}
            onChange={(v) => set("counterDue", v)}
          />
        </Field>

        <Field label={t("agent.flows.offer.nextStep", { defaultValue: "Next step" })}>
          <TextInput
            value={form.nextStep}
            onChange={(v) => set("nextStep", v)}
            placeholder="e.g. Await buyer response; chase Friday"
          />
        </Field>

        <Field label={t("agent.flows.offer.notes", { defaultValue: "Notes" })}>
          <TextArea value={form.notes} onChange={(v) => set("notes", v)} rows={3} />
        </Field>
      </ModalBody>

      <ModalFooter
        leftHint={
          saveError ? (
            <span style={{ color: "var(--ag-warn)" }}>{saveError}</span>
          ) : (
            t("agent.flows.escCancel", { defaultValue: "Esc to cancel" })
          )
        }
      >
        <Btn ghost onClick={onClose} disabled={saving}>
          {t("agent.flows.cancel", { defaultValue: "Cancel" })}
        </Btn>
        <Btn primary onClick={handleSave} disabled={saving}>
          {saving
            ? t("agent.flows.offer.saving", { defaultValue: "Saving…" })
            : t("agent.flows.offer.save", { defaultValue: "Save offer" })}
        </Btn>
      </ModalFooter>
    </ModalShell>
  );
}
